<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <BaseContentCard
      :title="$t(titleKey)"
      :description="$t(descriptionKey)"
      class="kds-color-background-purple-subtle"
    >
      <template #description>
        <p
          class="kds-typography-paragraph-large"
          v-html="$t(descriptionKey)"
        />
      </template>
      <template #illustration>
        <div
          role="img"
          aria-label="Doctor Outreach Info Banner"
          v-html="primerBackgroundSvg"
        />
      </template>
    </BaseContentCard>
    <template #actions>
      <BaseButton
        :loading="isLoading"
        data-qa="submit"
        @click="navigateToNext"
      >
        {{ $t(ctaKey) }}
      </BaseButton>
      <BaseButton
        v-if="isResubscription"
        variant="outlined"
        :text="$t('eu__doctor_outreach_primer__resubscription__secondary_cta')"
        data-qa="has-prescription"
        @click="navigateToCodeQuestion"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import useAssessmentStore from '@shared/modules/assessment/stores/useAssessmentStore.js'
import BaseButton from '@shared/components/BaseButton.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import BaseContentCard from '@shared/components/BaseContentCard.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import primerBackgroundSvg from '@eu/app/assets/img/doctor-outreach-primer-background.svg?raw'
import * as apiRequest from '@shared/api-endpoints/index.js'
import Tracker from '@shared/Tracker.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useFeatureFlagStore from '@shared/stores/useFeatureFlagStore.ts'
import useIsResubscription from '@shared/composables/useIsResubscription.ts'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'

const assessmentStore = useAssessmentStore()
const featureFlagStore = useFeatureFlagStore()
const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.DOCTOR_OUTREACH_PRIMER,
)

// Renewing users enter the funnel here (see the EU router hooks), so this screen
// carries the renewal copy and the "I already have one" escape hatch.
const isResubscription = useIsResubscription()
const webOptimizedFunnelOrResubscription = computed(
  () => featureFlagStore.flags.webOptimizedFunnel || isResubscription.value,
)
const isLoading = ref(false)
let assessmentConfigPromise = null
const titleKey = computed(() =>
  isResubscription.value
    ? 'eu__doctor_outreach_primer__resubscription__header'
    : featureFlagStore.flags.webOptimizedFunnel
      ? 'eu__doctor_outreach_primer__header__web'
      : 'eu__doctor_outreach_primer__header',
)
const descriptionKey = computed(() =>
  isResubscription.value
    ? 'eu__doctor_outreach_primer__resubscription__description'
    : featureFlagStore.flags.webOptimizedFunnel
      ? 'eu__doctor_outreach_primer__description__web'
      : 'eu__doctor_outreach_primer__description',
)
const ctaKey = computed(() =>
  webOptimizedFunnelOrResubscription.value
    ? 'generic_action_continue'
    : 'eu__doctor_outreach_primer__cta',
)

// Renewals used to answer this at PrescriptionQuestion; keep the activation
// timeline intact now that the two CTAs here carry the same answer.
const navigateToCodeQuestion = () => {
  Tracker.trackButtonSelect('has_prescription', 'doctor_outreach_primer')
  apiRequest.sendSubscriptionActivationEntry({ type: 'user_has_prescription' })
  navigateNext('hasPrescription')
}

const navigateToNext = async () => {
  if (isLoading.value) return

  isLoading.value = true

  if (isResubscription.value) {
    Tracker.trackButtonSelect('no_prescription', 'doctor_outreach_primer')
    apiRequest.sendSubscriptionActivationEntry({
      type: 'user_has_no_prescription',
    })
  }

  try {
    // the route's next reads currentQuestionnaireRoute from the ctx — make
    // sure the preloaded config has resolved before navigating
    await assessmentConfigPromise
    await navigateNext('needsPrescription')
  } catch (error) {
    console.error('Failed to continue from the doctor-outreach primer:', error)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  if (webOptimizedFunnelOrResubscription.value) return

  assessmentConfigPromise = assessmentStore
    .getQuestionnaireConfig()
    .catch((error) => {
      console.error('Failed to preload questionnaire config:', error)
    })
})
</script>
